/**
 * Sample Minister Briefing Pack — used as the default state for §7.25.
 * Per Phase 9 plan: real pack assembly from pinned signals + scenarios is
 * the Phase 9.7 build; this sample wires the visual primitives end-to-end
 * so CD's R10 design is visible + testable.
 *
 * Mirrors CD R10's sample (Ebola Zaïre cross-border ring-vaccination
 * decision) so the rendered output matches the design reference 1:1.
 */

import type {
  PackHeader, RagIndicator, ScenarioForecast, DecisionOption,
  PhiaAssertion,
} from './types';

export const SAMPLE_PACK_HEADER: PackHeader = {
  classification: 'OFFICIAL-SENSITIVE',
  asOf: '2026-05-31T06:00:00Z',
  distribution: 'Health Min + Private Office',
  packId: 'BM-2271',
  readTimeMin: 2,
  page: 1,
  totalPages: 2,
};

export const SAMPLE_BLUF_ASSERTIONS: { likely: PhiaAssertion; growth: PhiaAssertion } = {
  likely: { word: 'likely',        confidence: 'Moderate' },
  growth: { word: 'highly likely', confidence: 'High' },
};

export const SAMPLE_BLUF_PROSE = `A filovirus outbreak in eastern DRC is {likely} to cross into neighbouring jurisdictions within 14 days absent additional measures. Case growth is {growth} still in its exponential phase. A decision on cross-border vaccination support is required now.`;

export const SAMPLE_DECISION_REQUIRED = {
  text: 'Approve cross-border ring-vaccination support package',
  byTime: '1800Z today',
};

export const SAMPLE_RAG: readonly RagIndicator[] = [
  { name: 'Transmission',       status: 'red',   value: 'exponential' },
  { name: 'Health-system load', status: 'amber', value: 'strained' },
  { name: 'Cross-border risk',  status: 'red',   value: 'rising' },
  { name: 'Vaccine supply',     status: 'amber', value: '12k ready' },
  { name: 'Public trust',       status: 'amber', value: 'fragile' },
  { name: 'Containment',        status: 'red',   value: 'behind' },
];

export const SAMPLE_RECOMMENDATION = 'Option B — fund ring-vaccination + regional coordination cell.';

export const SAMPLE_SCENARIOS: readonly [ScenarioForecast, ScenarioForecast, ScenarioForecast] = [
  {
    kind: 'best',  label: 'Best case',
    peak: '1.2k', when: 'wk 4',
    phia: 'realistic possibility', confidence: 'Low',
    spark: synthSpark(0.45),
  },
  {
    kind: 'central', label: 'Central',
    peak: '4.8k', when: 'wk 6',
    phia: 'likely', confidence: 'Moderate',
    isPlanningCase: true,
    spark: synthSpark(0.60),
  },
  {
    kind: 'reasonable-worst-case', label: 'Reasonable worst case',
    peak: '18k', when: 'wk 9',
    phia: 'improbable', confidence: 'Low',
    spark: synthSpark(0.80),
  },
];

export const SAMPLE_OPTIONS: readonly DecisionOption[] = [
  {
    id: 'A', action: 'Maintain current posture',
    averted: '—', cost: '$0', political: 'Low', legal: 'Existing',
    dissent: null,
  },
  {
    id: 'B', action: 'Fund ring-vaccination + coordination cell',
    averted: '3.1k (CI 1.4–5.8k)', cost: '$4.2M (CI 3–6M)',
    political: 'Moderate', legal: 'Emergency reg',
    isRecommended: true,
    dissent: 'Two modellers hold that supply constraints make the averted-cases CI optimistic; real effect may be ~40% lower.',
    dissentingExpert: 'Modelling cell (2 dissenters of 5)',
  },
  {
    id: 'C', action: 'Full cross-border travel measures',
    averted: '5.0k (CI 2–9k)', cost: '$31M + trade',
    political: 'High', legal: 'Needs new authority',
    dissent: 'Legal adviser dissents: authority is contestable and may not survive challenge; economic cost likely understated.',
    dissentingExpert: 'Senior legal adviser',
  },
];

/** Gaussian-ish sparkline at a given peak position 0-1. */
function synthSpark(peakPos: number): number[] {
  const n = 24, out: number[] = [];
  for (let i = 0; i < n; i++) {
    const t = i / n;
    out.push(Math.exp(-Math.pow((t - peakPos) / 0.22, 2)));
  }
  return out;
}
